import { inject, Injectable } from '@angular/core';

import { mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
  AppSettingsApiActions,
  AppSettingsModalActions,
  AppSettingsResolverActions,
  AppSettingsViewActions,
} from './app-settings.actions';

import {
  ApplicationSetting,
  ApplicationSettingLink,
  ApplicationSettingsLinkService,
  ApplicationSettingsService,
  MutateApplicationSettings,
} from '@era/settings-sdk';
import { CategoryType } from '@rocksoft/shared';
import { compare } from 'fast-json-patch';
import { concat, EMPTY } from 'rxjs';
import { safeMap } from '../effects.helpers';
import { CategoryApplicationSetting } from './app-settings.state';

@Injectable()
export class AppSettingsEffects {
  private readonly appSettingsLinkService = inject(ApplicationSettingsLinkService);
  private readonly appSettingsService = inject(ApplicationSettingsService);
  private readonly actions$ = inject(Actions);

  settingsLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppSettingsResolverActions.appSettingsRequested),
      mergeMap(({ categoryType, categoryId, bundleIdentifier }) => {
        return safeMap(
          this.appSettingsLinkService.getApplicationSettingsForCategory({
            categoryType,
            categoryId,
            bundleIdentifier,
          }),
          (settings) =>
            AppSettingsApiActions.appSettingsLoaded({
              settings: this.mapToCategoryApplicationSetting(settings, categoryType, categoryId, bundleIdentifier),
            }),
          (error) => AppSettingsApiActions.appSettingsLoadedError({ error })
        );
      })
    );
  });

  settingsUpdatedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppSettingsModalActions.appSettingsUpdated),
      mergeMap((action) => {
        // Get all the information from the action payload.
        const configurationId = action.configurationId;
        const categoryType = action.categoryType;
        const categoryId = action.categoryId;
        const bundleIdentifier = action.bundleIdentifier;

        // Existing settings use the canonical id from GET. New settings still use the legacy derived id.
        const settingId = configurationId ?? `${categoryId}-${bundleIdentifier}`;

        // Map the new settings to the model which the endpoints require.
        const mappedNewSettings = action.newSettings.map((x) => this.mapToMutateApplicationSetting(x));

        // Map the old settings to the model which the endpoints require.
        const mappedOldSettings = action.oldSettings.map((x) => this.mapToMutateApplicationSetting(x));

        // Patch the application settings itself.
        const jsonPatch = compare(mappedOldSettings, mappedNewSettings);
        const patchApplicationSettings$ =
          jsonPatch.length > 0
            ? safeMap(
                this.appSettingsService.patchApplicationSettings({ id: settingId, operation: jsonPatch }),
                () =>
                  AppSettingsApiActions.appSettingsUpdated({
                    categoryType: categoryType,
                    categoryId: categoryId,
                    bundleIdentifier: bundleIdentifier,
                    settings: action.newSettings,
                  }),
                (error) => AppSettingsApiActions.appSettingsUpdatedError({ error })
              )
            : EMPTY;

        // Link settings configuration to category when none exist yet.
        const addSettingsToCategory$ =
          mappedOldSettings.length === 0
            ? this.addSettingsToCategory(
                categoryType,
                categoryId,
                this.mapToApplicationSettingLink(bundleIdentifier, settingId)
              )
            : EMPTY;

        // Unlink settings configuration from category when none exist anymore.
        const deleteSettingsFromCategory$ =
          mappedNewSettings.length === 0
            ? this.deleteSettingsFromCategory(categoryType, settingId, categoryId, bundleIdentifier)
            : EMPTY;

        // Combine all the endpoint calls together. It only calls an endpoint when its actually needed.
        return concat(patchApplicationSettings$, addSettingsToCategory$, deleteSettingsFromCategory$);
      })
    );
  });

  deleteSettingsConfigurationEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppSettingsViewActions.appSettingsConfigurationRemoveRequested),
      mergeMap(({ configurationId, categoryType, categoryId, bundleIdentifier }) => {
        const settingId = `${categoryId}-${bundleIdentifier}`;

        const deleteSettings$ = safeMap(
          this.appSettingsService.deleteApplicationSettings({
            id: configurationId ?? settingId,
          }),
          () =>
            AppSettingsApiActions.appSettingsUpdated({
              categoryType,
              categoryId,
              bundleIdentifier,
              settings: [],
            }),
          (error) => AppSettingsApiActions.appSettingsUpdatedError({ error })
        );

        const deleteFromCategory$ = this.deleteSettingsFromCategory(
          categoryType,
          settingId,
          categoryId,
          bundleIdentifier
        );
        return concat(deleteSettings$, deleteFromCategory$);
      })
    );
  });

  private mapToCategoryApplicationSetting(
    settings: ApplicationSetting[],
    categoryType: CategoryType,
    categoryId: string,
    bundleIdentifier: string
  ): CategoryApplicationSetting[] {
    return settings.map((s) => ({
      ...s,
      categoryType: categoryType,
      categoryId: categoryId,
      bundleIdentifier: bundleIdentifier,
    }));
  }

  private mapToApplicationSettingLink(bundleIdentifier: string, applicationSettingId: string): ApplicationSettingLink {
    return {
      bundleIdentifier,
      applicationSettingId,
    };
  }

  private mapToMutateApplicationSetting(
    setting: Pick<CategoryApplicationSetting, 'key' | 'value' | 'isSecret'>
  ): Partial<MutateApplicationSettings> {
    const mappedSetting: Partial<MutateApplicationSettings> = {
      key: setting.key,
      value: setting.value,
    };

    if (setting.isSecret) {
      mappedSetting.isSecret = true;
    }

    return mappedSetting;
  }

  private addSettingsToCategory(
    categoryType: CategoryType,
    categoryId: string,
    applicationSettingLink: ApplicationSettingLink
  ) {
    return safeMap(
      this.appSettingsLinkService.postApplicationSettingsForCategory({
        categoryType,
        categoryId,
        bundleIdentifier: applicationSettingLink.bundleIdentifier,
        applicationSettingLink,
      }),
      () => AppSettingsApiActions.appSettingsLinkedToCategory({ applicationSettingLink, categoryId }),
      (error) => AppSettingsApiActions.appSettingsLinkedError({ error })
    );
  }

  private deleteSettingsFromCategory(
    categoryType: CategoryType,
    settingId: string,
    categoryId: string,
    bundleIdentifier: string
  ) {
    return safeMap(
      this.appSettingsLinkService.deleteApplicationSettingsForCategory({
        categoryType,
        categoryId,
        bundleIdentifier,
        settingId,
      }),
      () => AppSettingsApiActions.appSettingsUnlinkedFromCategory({ settingId }),
      (error) => AppSettingsApiActions.appSettingsUnlinkedError({ error })
    );
  }
}
